/**
 * Usage Analytics API service.
 *
 * Reads the aggregates the proxy builds from this app's own event stream (see
 * `src/analytics/usageSink.ts` for the write side). The proxy authorizes
 * `space.people.usage_analytics.view` on this route, so the UI guard is cosmetic —
 * a caller without the permission gets a 403 whatever the client does.
 *
 * Everything here is aggregate — counts per day, per feature, per view — with one deliberate
 * exception, `fetchBoardUsage`, which is scoped and explained where it is defined.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export interface UsageDailyPoint {
  /** `YYYY-MM-DD`, Berlin day. */
  date: string;
  events: number;
  users: number;
}

/** One page (or page+tab) and every event recorded on it in the range. */
export interface EventSection {
  /** `ta-action-boards/feedback`, `equity-calculator`, or `unattributed`. */
  section: string;
  total: number;
  events: { name: string; count: number }[];
}

export interface UsageSummary {
  range: { from: string; to: string; days: number };
  totals: {
    events: number;
    activeUsers: number;
    activeDays: number;
    errors: number;
    errorRate: number;
  };
  daily: UsageDailyPoint[];
  topEvents: { name: string; count: number }[];
  /**
   * Every event grouped by the page/tab it fired on, biggest section first, uncapped. Optional:
   * a proxy revision older than this field simply omits it, and the card falls back to the
   * ranked top ten rather than rendering empty.
   */
  eventsBySection?: EventSection[];
  topViews: { view: string; count: number }[];
  errorSources: { source: string; count: number }[];
  /**
   * Every event name seen in the range — the whole set, not the ranked top ten. This is what makes
   * "which features fired zero times" answerable; a truncated list cannot distinguish an event
   * that ranked eleventh from one that never happened.
   */
  observedEvents: string[];
  /**
   * How many DISTINCT people used each page (`usersByView`) and each tab (`usersBySection`) in
   * the range. Reach, not volume — the top of the page tree leads on this, because one person
   * refreshing all week is not an adopted page and the event count cannot tell the two apart.
   *
   * Optional: a proxy revision older than this omits both, and the tree falls back to counts.
   */
  usersByView?: Record<string, number>;
  usersBySection?: Record<string, number>;
  /** True when the active-user query hit its document ceiling and counts are a floor. */
  truncated: boolean;
  generatedAt: string;
}

/** One TA action board — which is to say, one recruiter, since a board has exactly one owner. */
export interface BoardUsageRow {
  email: string;
  name: string;
  /** Events on the board itself. Zero is a real answer and the one worth acting on. */
  total: number;
  activeDays: number;
  /** `YYYY-MM-DD` of their last day on the board, or null if they had none in the range. */
  lastActive: string | null;
  /** Tab slug → events. `unattributed` is board activity that carried no tab. */
  byTab: Record<string, number>;
  /**
   * Tab slug → event name → count. The "which features exactly" answer, and attributed by the
   * SECTION the event fired on rather than by its name, so nothing another page emits lands here.
   */
  byTabEvent: Record<string, Record<string, number>>;
}

export interface BoardUsage {
  view: string;
  range: { from: string; to: string; days: number };
  /** The roster, busiest board first. Owners only — nobody else's documents are even read. */
  recruiters: BoardUsageRow[];
  /**
   * True when the read hit its document ceiling, which would make a row's zero unreliable. The
   * panel then withholds the "this board was never opened" conclusion rather than stating one it
   * cannot stand behind.
   */
  truncated: boolean;
  generatedAt: string;
}

/**
 * Per-board usage of the TA action boards.
 *
 * The one named report on this board, and deliberately so: a board has one owner, so anonymising
 * the column would have been a naming trick rather than a privacy control. Scope is what makes it
 * defensible and the server enforces it — the boards only, nothing else in People Space, and only
 * roster members get a row. It answers one question: which parts of the board a recruiter has not
 * been shown yet, so support goes where it is needed.
 */
export async function fetchBoardUsage(days: number): Promise<BoardUsage> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/usage/boards?days=${days}`, {
    suppressErrorStatuses: [403],
  });
  if (res.status === 403) {
    throw new UsageAccessError('You do not have access to usage analytics.');
  }
  return res.json();
}

/**
 * What happened to booked interviews.
 *
 * Reason CODES only — the stored `cancelReason` also carries the candidate's own free-text note,
 * which is never aggregated. Rates are null rather than 0 when nothing was booked, so an empty
 * range cannot be misread as a perfect one.
 */
export interface ReasonCount {
  code: string;
  count: number;
}

export interface InterviewOutcomes {
  range: { days: number };
  totals: {
    booked: number;
    cancelled: number;
    rescheduled: number;
    rescheduledTwicePlus: number;
    cancelRate: number | null;
    rescheduleRate: number | null;
    medianLeadDays: number | null;
    /** Invited → booked. `scheduleSampleSize` is reported so a tiny sample is never read as a trend. */
    medianScheduleDays: number | null;
    p90ScheduleDays: number | null;
    scheduleSampleSize: number;
    /** Invite EMAIL sent → booked: the candidate's own response time, in hours. */
    medianBookAfterEmailHours: number | null;
    p90BookAfterEmailHours: number | null;
    bookAfterEmailSampleSize: number;
    cancelReasonMissing: number;
    /**
     * Bookings that carry no `bookedAt` and so cannot be placed in any window. Shown on the board:
     * "empty because the data is missing" and "empty because nobody used it" look identical and call
     * for opposite responses. Cancelled bookings written before Aug 2026 lost the field.
     */
    undatedBookings?: number;
  };
  cancelReasons: { code: string; count: number }[];
  rescheduleReasons: { code: string; count: number }[];
  /**
   * Who changed the interview, and how (Sep 2026). `cancelSource` / `rescheduleSource` on the
   * booking say whether the CANDIDATE did it on their manage page or FINN did it from the Log or a
   * request card; the two reason vocabularies differ, so the reasons are split the same way. Optional
   * because a proxy build from before this shipped answers without it.
   */
  changes?: {
    cancelled: { candidate: number; finn: number };
    rescheduled: { candidate: number; finn: number };
    cancelReasons: { candidate: ReasonCount[]; finn: ReasonCount[] };
    rescheduleReasons: { candidate: ReasonCount[]; finn: ReasonCount[] };
    /** How many times a moved booking was moved: `times` 4 means four or more. */
    rescheduleCountDistribution: { times: number; count: number }[];
    cancelNoticeMedianHours: number | null;
    cancelledWithin24h: number;
    cancelNoticeSampleSize: number;
    /** Bookings whose last change could not be written to Lever ("⚠ Not in Lever" on the Log). */
    leverSyncFailed: number;
  };
  byStage: {
    stage: string;
    booked: number;
    cancelled: number;
    rescheduled: number;
    cancelRate: number | null;
  }[];
  generatedAt: string;
}

/**
 * Outbound email counts. Counts by kind, stage and day — never a recipient, subject or sender.
 * That is what lets this cross recruiters when the sent-email LIST deliberately does not.
 */
export interface SentEmailStats {
  range: { from: string; to: string };
  totals: { sent: number; withAttachment: number; activeDays: number };
  daily: { date: string; sent: number }[];
  byKind: { kind: string; label: string; count: number }[];
  byStage: { stage: string; count: number }[];
  generatedAt: string;
}

/**
 * Team-wide candidate-request statistics.
 *
 * `*Hours` figures are **time to DRAFT**, not time to send: the tool creates a Gmail draft and
 * deliberately cannot send, so the recruiter's final click is outside what any of this can see.
 * Median leads because response times are long-tailed; p90 rides beside it because the tail is the
 * part candidates notice.
 */
export interface ResponseStats {
  range: { from: string; to: string };
  totals: {
    inbound: number;
    drafted: number;
    awaiting: number;
    draftedRate: number | null;
    medianHours: number | null;
    p90Hours: number | null;
    fastestHours: number | null;
    slowestHours: number | null;
    excludedNegative: number;
    /** received → SENT, observed in the thread. Partial coverage; `repliesObserved` is the sample. */
    medianReplyHours: number | null;
    p90ReplyHours: number | null;
    repliesObserved: number;
    /** Inbound the policy deliberately will not auto-answer. */
    refused: number;
    refusedRate: number | null;
  };
  daily: { date: string; inbound: number; drafted: number }[];
  byCategory: { category: string; label: string; draftable: boolean; inbound: number; drafted: number }[];
  refusedCategories: { category: string; label: string; inbound: number }[];
  truncated: boolean;
  generatedAt: string;
}

/** One instrumented action on one tab, with enough beside it to read a count honestly. */
export interface SectionFeature {
  event: string;
  total: number;
  /** Distinct colleagues who used it in the range — what tells a habit from an adopted feature. */
  people: number;
  /** One entry per day in `days`, same order, zero-filled. */
  daily: number[];
  /** `YYYY-MM-DD` of the last day it fired at all, or null if it never did. */
  lastUsed: string | null;
}

export interface SectionFeatures {
  section: string;
  range: { from: string; to: string; days: number };
  /** Every day in the range, in order and with no gaps — a missing column reads as a broken chart. */
  days: string[];
  /** The section's own total per day, for the chart above the table. */
  totalDaily: number[];
  features: SectionFeature[];
  truncated: boolean;
  generatedAt: string;
}

/**
 * Everything recorded on ONE tab, over time.
 *
 * Fetched when a tab is opened rather than with the summary: the payload is days × events, and
 * nobody opens this board to read every tab's series at once.
 */
export async function fetchSectionFeatures(
  section: string,
  days: number
): Promise<SectionFeatures> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/usage/features?section=${encodeURIComponent(section)}&days=${days}`,
    { suppressErrorStatuses: [403] }
  );
  if (res.status === 403) {
    throw new UsageAccessError('You do not have access to usage analytics.');
  }
  return res.json();
}

/** Thrown when the proxy denies access (403) so the UI can explain instead of retrying. */
export class UsageAccessError extends Error {}

export async function fetchUsageSummary(days: number): Promise<UsageSummary> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/usage/summary?days=${days}`, {
    suppressErrorStatuses: [403],
  });
  if (res.status === 403) {
    throw new UsageAccessError('You do not have access to usage analytics.');
  }
  return res.json();
}

/**
 * Aggregate interview outcomes. Deliberately NOT the `/api/interviews` list endpoint, which
 * returns every candidate row — a dashboard has no business holding names and addresses in memory
 * to compute five numbers.
 */
export async function fetchInterviewOutcomes(days: number): Promise<InterviewOutcomes> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/interviews/outcome-stats?days=${days}`);
  return res.json();
}

/**
 * Candidate-request volume and response times, team-wide.
 *
 * A separate call from the usage summary on purpose: this reads real triage records rather than
 * the analytics event stream, and it stays useful even if the event pipeline is broken — which is
 * exactly the situation in which you would least want them to share a failure.
 */
export async function fetchSentEmailStats(days: number): Promise<SentEmailStats> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/ta-action-board/sent-email-stats?days=${days}`
  );
  return res.json();
}

export interface DraftFeedbackStats {
  range: { days: number };
  total: number;
  up: number;
  down: number;
  /** Null when nothing was rated — "0% unhappy" out of no votes would be a lie. */
  downRate: number | null;
  byCategory: { name: string; up: number; down: number; total: number; downRate: number }[];
  bySource: { name: string; up: number; down: number; total: number; downRate: number }[];
  /** Which surface the votes came from. One tab today; the field exists because that will change. */
  byTab: { name: string; up: number; down: number; total: number; downRate: number }[];
  /**
   * The votes with the mail they were a verdict on. All of this is permission-gated: the comment can
   * name a candidate, and `candidateText` IS the candidate's message. Down and up are capped
   * separately so a good week cannot push the complaints out of the list.
   */
  samples: {
    down: DraftFeedbackSample[];
    up: DraftFeedbackSample[];
  };
}

/** One rated draft: the verdict, the mail it answered, and what we produced. */
export interface DraftFeedbackSample {
  at: string;
  vote: 'up' | 'down';
  tab: string;
  category: string | null;
  draftSource: string | null;
  comment: string;
  subject: string;
  candidateText: string;
  draftText: string;
  /** True when the stored sample was trimmed, so the UI says so rather than implying it is whole. */
  truncated: boolean;
}

/**
 * How recruiters rate the drafts the Candidate Requests tab produces.
 *
 * Read from the stored votes rather than the analytics stream, like the other sections here: the
 * COMMENTS never reach an analytics sink at all (they can name a candidate), so the event stream
 * could not answer this even if it were healthy.
 */
export async function fetchDraftFeedbackStats(days: number): Promise<DraftFeedbackStats> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/ta-action-board/draft-feedback-stats?days=${days}`
  );
  return res.json();
}

export async function fetchResponseStats(days: number): Promise<ResponseStats> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/ta-action-board/response-stats?days=${days}`
  );
  return res.json();
}

/** One step of the sourcing funnel, in total or for one sourcing job (Oct 26 2026). */
export interface SourcingFunnel {
  checked: number;
  shortlisted: number;
  sent: number;
  followUps: number;
  linkedInFollowUpsDue: number;
  replied: number;
  hired: number;
  replyRate: number | null;
}

export interface SourcingStats {
  days: number;
  since: string;
  totals: SourcingFunnel & { hireRate: number | null };
  jobs: Array<SourcingFunnel & { id: string; roleTitle: string | null; createdAt: string; status: string; costUsd: number | null; costPerSent: number | null }>;
  /** Reply rate with and without common ground in the opener (PR 7); absent from older proxies. */
  commonGround?: Record<'with' | 'without', { sent: number; replied: number; replyRate: number | null }>;
  templates: Array<{ label: string; count: number }>;
  subjects: Array<{ label: string; count: number }>;
  /** `since`: when the AI cost started being recorded (Oct 26 2026); earlier calls are not in it. */
  costs: { since?: string; usd: number; perSent: number | null; perReply: number | null };
}

/** The Sourcing tab: funnel, templates, subjects and AI cost — counts only, no candidate. */
export async function fetchSourcingStats(days: number): Promise<SourcingStats> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/usage/sourcing?days=${days}`, { suppressErrorStatuses: [403] });
  if (res.status === 403) throw new UsageAccessError('You do not have access to usage analytics.');
  return res.json();
}
